import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";

import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { trackPageView } from "@/lib/tracker";
import { updateCanonicalForPath } from "@/lib/seo";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";

// Home is eager (it's the LCP route — splitting it would slow first paint)
import Home from "@/pages/Home";

// Everything else is lazy-loaded so we don't ship the whole app on first visit.
// This is the single biggest TBT / JS-execution win.
const Services = lazy(() => import("@/pages/Services"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Quote = lazy(() => import("@/pages/Quote"));
const Track = lazy(() => import("@/pages/Track"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const AdminBlog = lazy(() => import("@/pages/AdminBlog"));
const AdminInbox = lazy(() => import("@/pages/AdminInbox"));
const Careers = lazy(() => import("@/pages/Careers"));
const ValueProposition = lazy(() => import("@/pages/ValueProposition"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// LeadCaptureBar is below-the-fold and on a 12s delay anyway — split it out.
const LeadCaptureBar = lazy(() => import("@/components/LeadCaptureBar"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    // Visitor tracking — fire on every route change. Best-effort, never blocks.
    trackPageView(pathname);
    // Keep canonical + og:url in sync with the current route so each page reports its own URL.
    updateCanonicalForPath(pathname);
  }, [pathname]);
  return null;
}

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-[#0a0a0a]" data-testid="route-loading">
      <div className="font-mono text-xs tracking-[0.3em] uppercase text-white/40">Loading…</div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <>
      <Header />
      <main>
        <Suspense fallback={<RouteFallback />}>{children}</Suspense>
      </main>
      <Footer />
      <Suspense fallback={null}>
        <LeadCaptureBar />
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Shell><Home /></Shell>} />
              <Route path="/services" element={<Shell><Services /></Shell>} />
              <Route path="/about" element={<Shell><About /></Shell>} />
              <Route path="/contact" element={<Shell><Contact /></Shell>} />
              <Route path="/quote" element={<Shell><Quote /></Shell>} />
              <Route path="/track" element={<Shell><Track /></Shell>} />
              <Route path="/login" element={<Shell><Login /></Shell>} />
              <Route path="/register" element={<Shell><Register /></Shell>} />
              <Route path="/auth/callback" element={<Shell><AuthCallback /></Shell>} />
              <Route path="/payment/success" element={<Shell><PaymentSuccess /></Shell>} />
              <Route path="/payment/cancel" element={<Shell><PaymentCancel /></Shell>} />
              <Route path="/dashboard" element={<Shell><ProtectedRoute><Dashboard /></ProtectedRoute></Shell>} />
              <Route path="/blog" element={<Shell><Blog /></Shell>} />
              <Route path="/blog/:slug" element={<Shell><BlogPost /></Shell>} />
              <Route path="/careers" element={<Shell><Careers /></Shell>} />
              <Route path="/value-proposition" element={<Shell><ValueProposition /></Shell>} />
              <Route path="/admin/blog" element={<Shell><ProtectedRoute><AdminBlog /></ProtectedRoute></Shell>} />
              <Route path="/admin/inbox" element={<Shell><ProtectedRoute><AdminInbox /></ProtectedRoute></Shell>} />
              <Route path="*" element={<Shell><NotFound /></Shell>} />
            </Routes>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}
